/**
 * AuthSession — token custody + rotation for the Admin Panel (§11.1).
 *
 * TOKEN-STORAGE DESIGN (identity.yaml v1.1.0 ADMIN_WEB flow — ADOPTED M2-20):
 *  - The ACCESS token lives in this in-memory object only. It is never written
 *    to localStorage/sessionStorage/IndexedDB, never appears in a URL, and is
 *    attached exclusively as an Authorization header.
 *  - The REFRESH token travels as an **httpOnly, Secure, SameSite=Strict
 *    cookie** scoped to `/api/v1/auth`, set by the server on login/refresh —
 *    JavaScript NEVER sees it (the v1.1 body omits `refresh_token` for
 *    ADMIN_WEB), which removes the XSS-exfiltration channel entirely. The
 *    fetch transport sends `credentials: "include"` so the cookie flows, and
 *    session restore across a hard reload now works in real mode too (the
 *    M2-13 "reload = re-login" limitation is gone).
 *  - Because the cookie is invisible to script, "is a session restorable?" is
 *    unknowable client-side: boot always ATTEMPTS one silent cookie refresh
 *    (`tryRestore`) — a fresh visitor simply gets `401 REFRESH_INVALID` and
 *    lands on the login screen with no misleading "session expired" notice.
 *  - The mock transport emulates the same cookie inside the mock module
 *    (sessionStorage under a mock-only key), so session-restore UX is
 *    testable today WITHOUT the app ever persisting a token — the emulation
 *    disappears with the mock, not with a code change here.
 *
 * Refresh rotation is single-flight: concurrent 401s share one in-flight
 * refresh, because the contract treats refresh-token reuse as theft
 * (REFRESH_REUSE_DETECTED revokes the whole chain).
 */

import type { Transport } from "../api/transport";
import type { TokenPair } from "./contract";
import { refreshTokens } from "./identityApi";

/** Refresh this many seconds before the access token actually expires. */
const REFRESH_SKEW_SECONDS = 60;

export class AuthSession {
  private accessToken: string | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private inflightRefresh: Promise<boolean> | null = null;

  constructor(
    private readonly transport: Transport,
    /** Called when rotation fails MID-SESSION and the session is gone (guard → /login). */
    private readonly onSessionLost: () => void,
  ) {}

  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Adopts a fresh pair from login/refresh. v1.1: the body carries no
   * refresh token for ADMIN_WEB — the server already rotated the httpOnly
   * cookie; there is nothing for the client to store (§11.1).
   */
  adopt(tokens: TokenPair): void {
    this.accessToken = tokens.access_token;
    this.scheduleRefresh(tokens.expires_in);
  }

  /**
   * Boot-time restore: one silent cookie refresh. Never fires onSessionLost —
   * a visitor without a session is not a LOST session (no misleading
   * "expired" notice on a first visit, §14.3).
   */
  tryRestore(): Promise<boolean> {
    return this.refreshInternal(true);
  }

  /**
   * Mid-session rotation → new pair via the cookie. Single-flight; returns
   * false (and fires onSessionLost) when the chain is expired/revoked/reused.
   */
  refresh(): Promise<boolean> {
    return this.refreshInternal(false);
  }

  private refreshInternal(silent: boolean): Promise<boolean> {
    this.inflightRefresh ??= this.doRefresh(silent).finally(() => {
      this.inflightRefresh = null;
    });
    return this.inflightRefresh;
  }

  private async doRefresh(silent: boolean): Promise<boolean> {
    try {
      const pair = await refreshTokens(this.transport);
      this.adopt(pair);
      return true;
    } catch {
      this.destroy();
      if (!silent) this.onSessionLost();
      return false;
    }
  }

  /** Clears all client-side session state (server revocation is the caller's job). */
  destroy(): void {
    this.accessToken = null;
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private scheduleRefresh(expiresInSeconds: number): void {
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    const delayMs = Math.max(5, expiresInSeconds - REFRESH_SKEW_SECONDS) * 1000;
    this.refreshTimer = setTimeout(() => {
      void this.refresh();
    }, delayMs);
  }
}
